import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@lib/i18n";
import getConfig from "next/config";
import { dealType } from "types";
import api from "@api";
import { Button } from "components/shared/Button";
import CheckmarkIcon from "components/shared/Icons/Checkmark";
import CloseIcon from "components/shared/Icons/Close";
import CircularLoader from "components/shared/Icons/CircularLoader";
import { useDispatch } from "react-redux";
import {
  calculatePricesAction,
  postDealAction,
  removePromoCodeAction,
  setPromoCodeAction,
  setPromoCodeInvalidAction,
} from "redux/actions/dealActions";
import Typography from "components/shared/Typography";
import {
  CTA_SOL_TYPES,
  DEAL_TYPE,
  SESSION_STORAGE_KEYS,
} from "@shared/constants";
import getPriceAlgorithm from "@shared/PriceAlgorithm/getPriceAlgorithm";
import { dealStatusAllowsPromoCodeChanges } from "@shared/helper/dealStatusAllowsPromoCodeChanges";
import { useAppConfigSelector } from "redux/reducers/appConfig";
import { useIsUserTrustedSelector } from "redux/reducers/auth";
import { sessionStorageClient } from "utils/sessionStorageClient";
import WarningIcon from "components/shared/Icons/Warning";
import { isPromoCodeEndDateValid } from "@shared/helper/isPromoCodeEndDateValid";
import Icon from "@components/shared/Icons/Icon";
import { twJoin } from "tailwind-merge";

const PROMOCODE_STATUS = {
  INVALID: "invalid",
  VALID: "valid",
  WORKING: "working",
};

const StatusIcon = status => {
  switch (status) {
    case PROMOCODE_STATUS.INVALID:
      return (
        <Icon className="size-4 text-red" icon={<CloseIcon size="default" />} />
      );
    case PROMOCODE_STATUS.VALID:
      return (
        <Icon
          className="size-6 text-green"
          icon={<CheckmarkIcon checked={true} />}
        />
      );
    case PROMOCODE_STATUS.WORKING:
      return <CircularLoader size="md" />;
    default:
      return null;
  }
};

const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

const PromoCodeForm = ({ deal }) => {
  const { journey: journeyType, promoCode } = deal.financeSimulation;

  const priceAlgorithm = getPriceAlgorithm(COUNTRY);
  const appConfig = useAppConfigSelector();
  const isUserTrusted = useIsUserTrustedSelector();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [invalidCode, setInvalidCode] = useState(false);
  const [status, setStatus] = useState("");
  const [code, setCode] = useState(promoCode?.code || "");
  const [message, setMessage] = useState("");

  const inputRef = useRef(null);

  const isInvalid = status === PROMOCODE_STATUS.INVALID;

  const validate = async (e, forcedCode) => {
    e?.preventDefault();

    if ((!code || !code.trim().length) && !forcedCode) return;

    const {
      id: configurationId,
      lcdvCode,
    } = deal.carConfiguration.preconfiguration;

    const baseVehiclePrice = priceAlgorithm.getVehiclePriceBeforeDiscount(
      deal,
      appConfig
    );

    const payload = {
      businessModel: deal.businessModel,
      configurationId,
      journeyType: journeyType === "cash" ? "cash" : "finance",
      lcdv16: lcdvCode,
      netVehiclePrice: baseVehiclePrice.netPrice,
      promoCode: forcedCode ?? code,
    };

    setInvalidCode(false);
    setMessage("");

    try {
      setStatus(PROMOCODE_STATUS.WORKING);
      const { data } = await api.postValidatePromoCode(payload);

      if (!data.valid) {
        setStatus(PROMOCODE_STATUS.INVALID);
        setMessage(t("promoCodes.status.invalid"));
        sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);

        return;
      }

      dispatch(setPromoCodeAction(data));
      dispatch(calculatePricesAction());

      if (deal?.currentDeal?.token) {
        await dispatch(
          postDealAction(
            CTA_SOL_TYPES.CONFIGURATION_SAVED_BY_USER,
            DEAL_TYPE.DEAL,
            null,
            true
          )
        );
      } else if (!forcedCode) {
        sessionStorageClient.setItem(SESSION_STORAGE_KEYS.PROMOCODE, code);
      } else if (forcedCode) {
        setCode(forcedCode);
      }

      setStatus(PROMOCODE_STATUS.VALID);
      setMessage(t("promoCodes.status.accepted"));
    } catch (error) {
      setStatus(PROMOCODE_STATUS.INVALID);
      setMessage(t("promoCodes.status.invalid"));
      console.error(error);
    }
  };

  useEffect(() => {
    if (!deal.currentDeal && !isUserTrusted) {
      const sessionStorageSavedCode = sessionStorageClient.getItem(
        SESSION_STORAGE_KEYS.PROMOCODE
      );

      if (sessionStorageSavedCode) {
        setCode(sessionStorageSavedCode);
        validate(null, sessionStorageSavedCode);
      }
    }
  }, []);

  useEffect(() => {
    if (
      promoCode &&
      !invalidCode &&
      dealStatusAllowsPromoCodeChanges(deal.status) &&
      !isPromoCodeEndDateValid(promoCode.endDate)
    ) {
      promoCode.valid && dispatch(setPromoCodeInvalidAction());
      sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);
      setInvalidCode(true);
      setStatus(PROMOCODE_STATUS.INVALID);
      setMessage(t("promoCodes.status.expired"));
    } else if (
      promoCode &&
      !invalidCode &&
      dealStatusAllowsPromoCodeChanges(deal.status) &&
      deal.quantity !== 1
    ) {
      if (!isUserTrusted) {
        api.removePromoCodeFromSession();
      }

      dispatch(removePromoCodeAction());
      dispatch(calculatePricesAction());

      sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);

      if (deal?.currentDeal?.token) {
        dispatch(
          postDealAction(
            CTA_SOL_TYPES.CONFIGURATION_SAVED_BY_USER,
            DEAL_TYPE.DEAL,
            null,
            true
          )
        );
      }
    }
  }, [deal, promoCode]);

  const handleClearInput = () => {
    setMessage("");
    setStatus("");
    setCode("");
    setInvalidCode(false);
  };

  const handleClearInputClick = e => {
    e.preventDefault();

    if (!isInvalid) {
      return;
    }

    handleClearInput();
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (!promoCode) {
      handleClearInput();
    }
  }, [deal]);

  return (
    <div className="flex flex-col items-start border-t border-t-primaryOrange gap-4 pt-8 pb-1">
      <Typography className="text-sm font-semibold uppercase">
        {t("promoCodes.title")}
      </Typography>
      {deal.quantity === 1 ? (
        <form
          className="flex flex-col items-start justify-stretch gap-4 w-full sm:flex-row"
          onSubmit={validate}
        >
          <div className="relative w-full h-full">
            <input
              className="border border-greyLight rounded-sm p-2 pr-10 text-sm/5 h-10 w-full bg-white"
              data-id="promo-code-input"
              disabled={!!promoCode && !invalidCode}
              onChange={e => setCode(e.target.value)}
              placeholder={t("promoCodes.inputPlaceholder")}
              ref={inputRef}
              type="text"
              value={code}
            />
            <button
              className={twJoin(
                "grid place-items-center size-6 absolute top-2 right-2 select-none rounded-sm",
                isInvalid
                  ? "cursor-pointer transition-all duration-150 hover:scale-110 active:scale-90 hover:bg-primaryLight"
                  : "pointer-events-none"
              )}
              disabled={!isInvalid}
              onClick={handleClearInputClick}
              type="button"
            >
              {StatusIcon(status)}
            </button>
            <Typography className="text-xs/4 mt-1 pl-2">{message}</Typography>
          </div>
          <Button
            className="max-sm:w-full px-6 py-1.5 text-sm/4 m-0 min-w-25 h-10"
            data-id="promo-code-submit-button"
            disabled={!!promoCode && !invalidCode}
            size="small"
            type="submit"
            variant="secondary"
          >
            {t("promoCodes.submit")}
          </Button>
        </form>
      ) : (
        <div className="flex items-center gap-4">
          <Icon className="text-brightOrange size-9" icon={<WarningIcon />} />
          <Typography className="text-sm/5">
            {t("promoCodes.quantityError")}
          </Typography>
        </div>
      )}
    </div>
  );
};

PromoCodeForm.propTypes = {
  deal: dealType.isRequired,
};

export default PromoCodeForm;
